import { z } from 'zod';

/**
 * Zod schema defining grid cell positioning within a ComponentGrid.
 *
 * Uses 1-indexed row and column numbers to match CSS Grid conventions.
 * Optional span properties allow components to occupy multiple cells.
 *
 * @property row - Starting row number (1-indexed)
 * @property rowSpan - Number of rows to span (default: 1)
 * @property column - Starting column number (1-indexed)
 * @property columnSpan - Number of columns to span (default: 1)
 *
 * @example
 * // Component at row 2, column 1, spanning 2 columns
 * { row: 2, column: 1, columnSpan: 2 }
 *
 */
export const PositionType = z.object({
  row: z
    .number()
    .prefault(1)
    .describe('Row position (1-indexed). Row 1 = top for KPIs/metrics, rows 2-3 for charts, lower rows for tables'),
  rowSpan: z.number().prefault(1).describe('Number of rows to span (default: 1)'),
  column: z.number().prefault(1).describe('Column position (1-indexed)'),
  columnSpan: z.number().prefault(1).describe('Number of columns to span (default: 1)'),
});

/** @public Input type for PositionType (before transforms). */
export type PositionInput = z.input<typeof PositionType>;

/** Output type for PositionType (after transforms). */
export type Position = z.output<typeof PositionType>;

/**
 * Zod schema for Cognite Data Fusion instance identifiers.
 *
 * @property space - The space where the instance resides
 * @property externalId - Unique identifier within the space
 */
export const InstanceIdType = z.object({ space: z.string(), externalId: z.string() });

/** TypeScript type for CDF instance identifiers. */
export type InstanceIdType = z.infer<typeof InstanceIdType>;

export const SortDirectionType = z.enum(['asc', 'desc']);
